import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, Modal, TextInput, ActivityIndicator, Alert } from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { leaveAPI } from '../services/api';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { Toast } from '../components/common/Toast';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface Leave {
  id: string;
  fromDate: string;
  toDate: string;
  reason: string;
  appliedDate: string;
}

// Plain CSS for the raw HTML <input type="date"> below — kept independent of the
// RN StyleSheet system since that isn't guaranteed to produce a DOM-compatible
// style object. A real date picker beats asking a parent to type "YYYY-MM-DD".
const dateInputStyle: React.CSSProperties = {
  border: '1px solid #E2E8F0',
  borderRadius: 8,
  padding: '12px',
  fontSize: 14,
  color: '#1E293B',
  backgroundColor: '#F8FAFC',
  marginBottom: 12,
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export function LeavesScreen() {
  const { user, selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const loadLeaves = useCallback(async () => {
    if (!selectedStudent) {
      setIsLoading(false);
      return;
    }
    try {
      const res = await leaveAPI.getLeaveRequests(selectedStudent.id);
      // API returns startDate/endDate/createdDate, not fromDate/toDate/appliedDate —
      // matching those never existed, which is why every date rendered as "Invalid Date".
      const mapped = (res.data ?? []).map((l: any) => ({
        id: l.id || l.Id || '',
        fromDate: l.startDate || l.StartDate || '',
        toDate: l.endDate || l.EndDate || '',
        reason: l.reason || l.Reason || '',
        appliedDate: l.createdDate || l.CreatedDate || '',
      }));
      setLeaves(mapped);
    } catch (error) {
      Logger.error('Leaves', 'Failed to load leaves', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  const handleApplyLeave = async () => {
    if (!fromDate || !toDate || !reason.trim() || !user || !selectedStudent) return;
    try {
      setIsSubmitting(true);
      await leaveAPI.applyLeave({
        studentId: selectedStudent.id,
        fromDate,
        toDate,
        reason: reason.trim(),
      });
      setFromDate('');
      setToDate('');
      setReason('');
      setShowForm(false);
      setToastMessage('Leave application submitted');
      setShowToast(true);
      await loadLeaves();
    } catch (error) {
      Alert.alert('Error', 'Failed to apply leave');
      Logger.error('Leaves', 'Failed to apply leave', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const todayIsoDate = new Date().toISOString().split('T')[0];

  const renderItem = ({ item }: { item: Leave }) => {
    const days = Math.ceil(
      (new Date(item.toDate).getTime() - new Date(item.fromDate).getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

    return (
      <View style={styles.card}>
        <View style={styles.cardTopRow}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{days} day{days > 1 ? 's' : ''}</Text>
          </View>
        </View>
        <Text style={styles.cardDesc}>
          {new Date(item.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {' → '}
          {new Date(item.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Text>
        <Text style={styles.reason} numberOfLines={2}>{item.reason}</Text>
        <Text style={styles.cardDate}>
          Applied {new Date(item.appliedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </Text>
      </View>
    );
  };

  return (
    <WebPageShell title="Leaves">
      {/* Same dashed-border "add" affordance as Complaints' "Raise a concern". */}
      <TouchableOpacity style={styles.addBtn} onPress={() => setShowForm(true)}>
        <FeatherIcon name="plus" size={18} color="#F97316" />
        <Text style={styles.addBtnText}>Apply for leave</Text>
      </TouchableOpacity>

      {isLoading ? (
        <LoadingSpinner message="Loading leave applications..." />
      ) : leaves.length === 0 ? (
        <View style={styles.empty}>
          <FeatherIcon name="inbox" size={40} color={colors.textMuted} />
          <Text style={styles.emptyText}>No leave applications</Text>
        </View>
      ) : (
        <FlatList
          data={leaves}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          scrollEnabled
        />
      )}

      <Modal visible={showForm} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Apply for Leave</Text>
              <TouchableOpacity onPress={() => setShowForm(false)}>
                <FeatherIcon name="x" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>From Date <Text style={styles.required}>*</Text></Text>
            <input
              type="date"
              style={dateInputStyle}
              value={fromDate}
              min={todayIsoDate}
              disabled={isSubmitting}
              // Typed as any: this project's RN types shadow lib.dom, so
              // HTMLInputElement has no `value` as far as tsc is concerned.
              onChange={(e: any) => setFromDate(e.target.value)}
            />
            <Text style={styles.label}>To Date <Text style={styles.required}>*</Text></Text>
            <input
              type="date"
              style={dateInputStyle}
              value={toDate}
              // Can't end before it starts.
              min={fromDate || todayIsoDate}
              disabled={isSubmitting}
              onChange={(e: any) => setToDate(e.target.value)}
            />
            <Text style={styles.label}>Reason <Text style={styles.required}>*</Text></Text>
            <TextInput
              style={[styles.input, { marginTop: 8, minHeight: 120 }]}
              placeholder="Describe the reason for your leave request..."
              multiline
              numberOfLines={5}
              value={reason}
              onChangeText={setReason}
              editable={!isSubmitting}
            />

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowForm(false)}
                disabled={isSubmitting}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.submitBtn, isSubmitting && styles.submitBtnDisabled]}
                onPress={handleApplyLeave}
                disabled={isSubmitting || !fromDate || !toDate || !reason.trim()}>
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.submitBtnText}>Submit</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Toast
        visible={showToast}
        message={toastMessage}
        type="success"
        onDismiss={() => setShowToast(false)}
      />
    </WebPageShell>
  );
}

// Same tokens as ComplaintsScreen.web.tsx — the two "raise a request" pages
// (Complaints, Leave) should read as one design, not two separately built ones.
const createStyles = (colors: ThemeColors) => StyleSheet.create({
  addBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 14, borderWidth: 1, borderColor: '#F97316', borderStyle: 'dashed', borderRadius: 12, marginBottom: 20, backgroundColor: colors.card, gap: 8 },
  addBtnText: { color: '#F97316', fontWeight: '700', fontSize: 14 },
  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600' },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 12, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  cardTopRow: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 6 },
  badge: { backgroundColor: colors.primaryLight, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 16 },
  badgeText: { fontSize: 12, fontWeight: '700', color: colors.primary },
  cardDesc: { fontSize: 15, color: colors.textPrimary, fontWeight: '700', marginBottom: 6 },
  reason: { fontSize: 13, color: colors.textSecondary, marginBottom: 8, lineHeight: 18 },
  cardDate: { fontSize: 13, color: colors.textMuted, fontWeight: '500' },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'center', alignItems: 'center' },
  modal: { backgroundColor: colors.card, borderRadius: 16, padding: 20, maxHeight: '90%', width: '100%', maxWidth: 500 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  label: { fontSize: 13, fontWeight: '600', color: colors.textSecondary, marginBottom: 6 },
  required: { color: '#DC2626' },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 12, marginBottom: 16, color: colors.textPrimary, textAlignVertical: 'top' },
  modalFooter: { flexDirection: 'row', gap: 12 },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: colors.border, alignItems: 'center' },
  cancelBtnText: { fontSize: 14, fontWeight: '700', color: colors.textSecondary },
  submitBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: colors.primary, alignItems: 'center' },
  submitBtnDisabled: { opacity: 0.6 },
  submitBtnText: { fontSize: 14, fontWeight: '700', color: '#FFF' },
});
